<template>
  <span
    class="wishlist-toggler"
    :class="togglerClass"
  >
    <span
      class="wishlist-toggler__button"
      :class="{
        'wishlist-toggler__button--added': inWishlist
      }"
      @click="toggleWishlist"
    >
      <span :class="inWishlist ? 'icon-heart-solid' : 'icon-heart'" />
    </span>
    <span v-if="inWishlistCount > 0" class="wishlist-toggler__count">
      {{ inWishlistCount }}
    </span>
  </span>
</template>

<script setup lang="ts">
import { modalAuthId } from '@/constants/modalNames'

type Props = {
  productCode: string
  productName: string
  productCategorySlug: string
  productSlug: string
  updateInWishlistCount?: boolean
  inWishlistCount?: number
  offerCode: string
  hovered?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  updateInWishlistCount: false,
  inWishlistCount: 0
})

const { $vfm } = useNuxtApp()
const authUserStore = useAuthUserStore()
const wishlistOffersStore = useWishlistOffersStore()
const commonMetricsStore = useCommonMetricsStore()
const productStore = useProductStore()

const fetching = shallowRef(false)

const isAuthenticated = computed(() => {
  return authUserStore.isAuthenticated
})

const inWishlist = computed(() => {
  return wishlistOffersStore.offerCodes.has(props.offerCode)
})

const togglerClass = computed(() => ({
  'wishlist-toggler--show': props.hovered || inWishlist.value
}))

const toggleWishlist = () => {
  isAuthenticated.value
    ? toggleUserWishlist()
    : toggleGuestWishlist()
}

const toggleUserWishlist = () => {
  fetching.value = true

  try {
    if (!inWishlist.value) {
      wishlistOffersStore.addToWishlist(props.offerCode)

      commonMetricsStore.addMetricEvent({
        element: 'ms[wishlist-add-product]',
        event_type: 'click',
        event_content: JSON.stringify({
          productCode: props.productCode,
          offerCode: props.offerCode
        })
      })

      // window.fbq('track', 'AddToWishlist', {
      //   content_name: props.productName,
      //   content_ids: [props.productCode],
      //   content_type: 'product'
      // })

      if (props.updateInWishlistCount) {
        productStore.incrementWishListCount()
      }
    } else {
      wishlistOffersStore.removeFromWishlist(props.offerCode)

      commonMetricsStore.addMetricEvent({
        element: 'ms[wishlist-remove-product]',
        event_type: 'click',
        event_content: JSON.stringify({
          productCode: props.productCode,
          offerCode: props.offerCode
        })
      })

      if (props.updateInWishlistCount) {
        productStore.decrementWishlistCount()
      }
    }
  } finally {
    fetching.value = false
  }
}

const toggleGuestWishlist = () => {
  $vfm.open(modalAuthId, {
    form: 'login',
    loginCallback: toggleUserWishlist
  })
}

</script>

<style scoped lang="scss">
.wishlist-toggler {
  font-size: 2.4rem;

  @media only screen and (min-width: 1024px) {
    display: none;
  }

  @media only screen and (max-width: 1024px) {
    font-size: 2.2rem;
  }
  &--show {
    display: flex;
    align-items: center;
  }

  &__button {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 32px;
    width: 32px;
    border-radius: 4px;
    background-color: $white-color;
    cursor: pointer;
    color: #b9b9b9;

    &--added {
      color: $primary-color;
      display: inline-flex;
    }

    &:not(&--added):hover {
      color: #a2a2a2;
    }
  }

  &__count {
    font-size: 14px;
    color: #BEBEBE;
    margin-left: 5px;
  }
}
</style>
